import { Injectable } from '@angular/core';
import { Navigate } from '@ngxs/router-plugin';
import { Select, Store } from '@ngxs/store';
import { LinkedExecution } from 'app/library/course-catalog-listing/models/linked-execution.model';
import { CourseTemplate } from 'app/library/course-offering/models/course-template.model';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { ModuleFeatureService } from 'app/library/module-feature/services/module-feature.service';
import { FetchModuleManagers, FetchModuleManagersFailure, FetchModuleManagersSuccess } from 'app/library/module-managers/actions';
import { ModuleManagersState } from 'app/library/module-managers/models/module-managers.state';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { ModuleType } from 'app/library/module/enums/moduleType.enum';
import { BasicModule } from 'app/library/module/models/basic-module.model';
import { ELearningModule } from 'app/library/module/models/eLearning-module.model';
import { ModuleApiService } from 'app/library/module/services/module.api-service';
import { ModuleGlobalService } from 'app/library/module/services/module-global.service';
import { MyModulesState } from 'app/library/my-profile-modules/models/my-modules.state';
import { RelevantExecution } from 'app/library/program-item/models/relevant-execution.model';
import { CourseRelevantExecution } from 'app/library/relevant-execution/models/course-relevant-execution.model';
import { setScormLaunchReturnUrl } from 'app/library/scorm/utils/handle-scorm-launch-return.util';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { ModuleFeatureSwitchName } from 'app/projects/shared/src/lib/enums/module-feature-switch.enum';
import { HelperService } from 'app/projects/shared/src/lib/services/helper';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { Module } from '../../module/models/module.model';

@Injectable()
export class ModuleManagersService {
    @Select(ModuleManagersState.getEntities)
    moduleParticipant$: Observable<ModuleUser[]>;

    user: User;
    isCurrentUserTenantAdmin: boolean;
    isCurrentUserModuleManager: boolean;

    get hasTenantAdminOrCourseWriteReadPermission(): boolean {
        return (
            this.user.permissions.includes(AdminPermissions.COURSES_WRITE) ||
            this.user.permissions.includes(AdminPermissions.COURSES_READ) ||
            this.user.permissions.includes(AdminPermissions.TENANT_MANAGEMENT)
        );
    }

    get hasTenantAdminOrModulesWriteReadPermission(): boolean {
        return (
            this.user.permissions.includes(AdminPermissions.USERS_WRITE) ||
            this.user.permissions.includes(AdminPermissions.MODULES_READ) ||
            this.user.permissions.includes(AdminPermissions.TENANT_MANAGEMENT)
        );
    }

    constructor(
        private _store: Store,
        private _actionHelper: NgxsActionHelper,
        private _moduleFeatureService: ModuleFeatureService,
        private _moduleApiService: ModuleApiService,
        private _moduleGlobalService: ModuleGlobalService,
        private _helperService: HelperService
    ) {
        this.user = this._store.selectSnapshot(UserState.getMyInfo);

        this.isCurrentUserTenantAdmin = this.user.permissions.includes(AdminPermissions.TENANT_MANAGEMENT);
        this.isCurrentUserModuleManager = this.getModuleManagers()?.some((m) => m.userId === this.user.id);
    }

    getModuleManagers(): ModuleUser[] {
        return this._store.selectSnapshot(ModuleManagersState.getEntities);
    }

    async fetchManagersByModuleId(moduleId: string): Promise<boolean> {
        this._store.dispatch(new FetchModuleManagers(moduleId));

        return await this._actionHelper.race(FetchModuleManagersSuccess, FetchModuleManagersFailure).toPromise();
    }

    async goToModule(
        userId: string,
        module: BasicModule | ELearningModule | CourseTemplate | RelevantExecution | CourseRelevantExecution | LinkedExecution,
        tab: string = 'overview',
        goToParticipantMode: boolean = false,
        isEditMode: boolean = false,
        returnUrl?: string
    ): Promise<void> {
        const pendingReturnUrl = returnUrl;

        const isFetched = await this.fetchManagersByModuleId(module.id);

        // If response is false, we had an error while fetching ManagersByModuleId
        if (!isFetched) {
            return;
        }

        let moduleManager = null;
        const moduleManagers = this.getModuleManagers();

        await this._moduleFeatureService.fetchModuleFeatures(module.id);
        const isModuleFeatureOverviewTabActive = this._moduleFeatureService.checkModuleFeatureStatus(ModuleFeatureSwitchName.OverviewTab);

        if (moduleManagers) {
            moduleManager = moduleManagers.find((manager) => manager.userId === userId);
        }

        // NEW: check if we should go to edit
        const isOverviewTab = tab === 'overview';
        const shouldGoToEdit =
            isEditMode ||
            tab.includes('edit') ||
            (isOverviewTab &&
                (this.hasTenantAdminOrCourseWriteReadPermission || this.hasTenantAdminOrModulesWriteReadPermission || this.isCurrentUserTenantAdmin || moduleManager));

        if (moduleManager || !goToParticipantMode) {
            if (
                (tab === 'preview/overview' || tab.startsWith('preview/e-learning/overview') || tab === 'preview/program-overview') &&
                (module.kind === ModuleKind.Template || module.kind === ModuleKind.ProgramTemplate)
            ) {
                this._navigateToModule(`/modules/${module.id}/${tab}`, pendingReturnUrl);
            } else {
                this._navigateToModule(
                    `/modules/${module.id}/edit/${module.type === ModuleType.ELearning ? '/e-learning/' : ''}${tab}`,
                    pendingReturnUrl
                );
            }
        } else {
            if (this.isCurrentUserTenantAdmin && (module.kind === ModuleKind.Template || module.kind === ModuleKind.ProgramTemplate) && !goToParticipantMode) {
                if (tab === 'preview/overview' || tab.startsWith('preview/e-learning/overview') || tab === 'preview/program-overview') {
                    this._navigateToModule(`/modules/${module.id}/${tab}`, pendingReturnUrl);
                } else {
                    this._navigateToModule(
                        `/modules/${module.id}/${shouldGoToEdit ? 'edit/' : ''}${module.type === ModuleType.ELearning ? '/e-learning/' : ''}${tab}`,
                        pendingReturnUrl
                    );
                }
            } else {
                if (tab === 'overview') {
                    this.handleOverviewNavigation(module, isModuleFeatureOverviewTabActive, shouldGoToEdit, pendingReturnUrl);
                } else {
                    this._navigateToModule(
                        `/modules/${module.id}/${shouldGoToEdit ? 'edit/' : ''}${module.type === ModuleType.ELearning ? 'e-learning/' : ''}${tab}`,
                        pendingReturnUrl
                    );
                }
            }
        }
    }

    private _navigateToModule(path: string, returnUrl?: string): void {
        if (returnUrl) {
            this._moduleGlobalService.setParentUrl(returnUrl);
        }

        this._store.dispatch(new Navigate([path]));
    }

    private handleOverviewNavigation(
        module: Module | CourseTemplate | RelevantExecution | CourseRelevantExecution | LinkedExecution,
        isModuleFeatureOverviewTabActive: boolean,
        shouldGoToEdit: boolean,
        returnUrl?: string
    ): void {
        const shouldSkipOverviewPage =
            !isModuleFeatureOverviewTabActive &&
            (('selfPaced' in module && module.selfPaced) || module.type === ModuleType.ELearning);

        if (!shouldSkipOverviewPage) {
            this._navigateToModule(
                `/modules/${module.id}/${shouldGoToEdit ? 'edit' : ''}/${module.type === ModuleType.ELearning ? 'e-learning/overview/content' : 'overview'}`,
                returnUrl
            );
        } else if (module.type === ModuleType.ELearning) {
            const currentModuleUser = (this._store.selectSnapshot(MyModulesState.getEntities) || []).find((moduleUser) => moduleUser.moduleId === module.id);

            if (currentModuleUser && !currentModuleUser.isParticipant) {
                this._helperService.showSnackBarMessage('E_LEARNING.MESSAGE.CANT_LAUNCH_NOT_PARTICIPANT', 'OK', SnackBarTime.medium);
                this._navigateToModule(
                    `/modules/${module.id}/${shouldGoToEdit ? 'edit/' : ''}e-learning/overview/content`,
                    returnUrl
                );
                return;
            }

            this._moduleApiService.getModuleLaunchCourseUrl(module.id).subscribe((response) => {
                // Content-only: return to catalog / my courses (prefer parentUrl, fallback my courses)
                const scormReturnUrl = returnUrl || this._moduleGlobalService.getParentUrl() || window.location.pathname || '/modules';
                if (returnUrl) {
                    this._moduleGlobalService.setParentUrl(returnUrl);
                }
                setScormLaunchReturnUrl(scormReturnUrl);

                this._store.dispatch(
                    new Navigate([`/modules/${module.id}/e-learning/launch`], { scormLearning: true, scormUrl: response.launchLink })
                );
            });
        } else {
            this._navigateToModule(`/modules/${module.id}/content/sections`, returnUrl);
        }
    }
}
